import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import React, { ReactNode, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { radius } from "src/design/tokens";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: "default" | "destructive";
  loading?: boolean;
  eyebrow?: string;
  acknowledgement?: string;
  typedConfirmation?: string;
  /**
   * Stable `data-testid`s for the e2e suite: `<prefix>-dialog` on the dialog, `<prefix>-check` on
   * the acknowledgement checkbox, `<prefix>-confirm` and `<prefix>-cancel` on the buttons.
   */
  testIdPrefix?: string;
  /** Replaces `<testIdPrefix>-confirm` where a contract names the confirm button on its own. */
  confirmTestId?: string;
  /** Keeps confirm disabled, for example while the numbers it confirms are loading. */
  confirmDisabled?: boolean;
  /** Extra content under the explanation, for example a notice that the numbers changed. */
  children?: ReactNode;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Confirmation dialog (design `hasConfirm`): red eyebrow, 24 px title, the explanation in a
 * surfaceAlt panel, an optional acknowledgement checkbox, a ghost cancel and a strong confirm
 * pill (red for the destructive tone).
 */
export const ConfirmDialog = ({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  tone = "default",
  loading = false,
  eyebrow,
  acknowledgement,
  typedConfirmation,
  testIdPrefix,
  confirmTestId,
  confirmDisabled = false,
  children,
  onConfirm,
  onClose,
}: ConfirmDialogProps) => {
  const { t } = useTranslation();
  const uid = useId();
  const titleId = `${uid}-title`;
  const bodyId = `${uid}-body`;
  const [acknowledged, setAcknowledged] = useState(false);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!open) {
      setAcknowledged(false);
      setTyped("");
    }
  }, [open]);

  const testId = (part: string) =>
    testIdPrefix ? { "data-testid": `${testIdPrefix}-${part}` } : {};
  const gated =
    (acknowledgement !== undefined && !acknowledged) ||
    (typedConfirmation !== undefined && typed !== typedConfirmation);

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { maxWidth: 540 }, ...testId("dialog") }}
    >
      {eyebrow ? (
        <Typography
          variant="overline"
          component="div"
          sx={{
            display: "block",
            color: "dangerInk",
            letterSpacing: ".12em",
            lineHeight: 1,
            px: 3,
            pt: 2.5,
          }}
        >
          {eyebrow}
        </Typography>
      ) : null}
      <DialogTitle
        id={titleId}
        component="h2"
        sx={{ fontSize: 24, pt: eyebrow ? 1.5 : undefined, pb: 1.5 }}
      >
        {title}
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          // The body panel opens the content, so it needs none of the theme's room for a field label.
          ".MuiDialogTitle-root + &": { pt: 0 },
        }}
      >
        <DialogContentText
          id={bodyId}
          component="div"
          sx={(theme) => ({
            p: 2,
            borderRadius: `${radius.panel}px`,
            bgcolor: theme.palette.surfaceAlt,
          })}
        >
          {body}
        </DialogContentText>
        {acknowledgement !== undefined ? (
          <Box>
            <FormControlLabel
              sx={{
                m: 0,
                alignItems: "flex-start",
                gap: 1,
                "& .MuiFormControlLabel-label": {
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: 1.4,
                },
              }}
              control={
                <Checkbox
                  checked={acknowledged}
                  inputProps={
                    testId(
                      "check",
                    ) as React.InputHTMLAttributes<HTMLInputElement>
                  }
                  onChange={(event) => setAcknowledged(event.target.checked)}
                  sx={{ p: 0, mt: "1px" }}
                />
              }
              label={acknowledgement}
            />
          </Box>
        ) : null}
        {children}
        {typedConfirmation !== undefined ? (
          <TextField
            fullWidth
            size="small"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            label={t("console.typeToConfirm", {
              defaultValue: "Type {{word}} to confirm",
              word: typedConfirmation,
            })}
            autoComplete="off"
          />
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.25 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          color="inherit"
          {...testId("cancel")}
        >
          {cancelLabel ?? t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          color={tone === "destructive" ? "error" : "secondary"}
          onClick={onConfirm}
          disabled={gated || loading || confirmDisabled}
          {...(confirmTestId
            ? { "data-testid": confirmTestId }
            : testId("confirm"))}
          sx={{ "&.Mui-disabled": { opacity: 0.45 } }}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
